import React, { useState } from 'react';
import { adminDb } from '../../services/adminService';

const actionGroups = [
  { label: 'Family & lifecycle', values: ['family_read_only', 'family_restore_writes', 'family_expedited_deletion_schedule', 'family_purged'] },
  { label: 'Children & invitations', values: ['child_deactivate', 'child_reactivate', 'invitation_renew', 'invitation_delivery'] },
  { label: 'Support & billing', values: ['support_note_add', 'stripe_recheck'] },
  { label: 'Portal activity', values: ['dashboard_view', 'family_search', 'family_view', 'support_notes_view', 'audit_view', 'audit_search'] },
];

const emptyFilters = { family: '', actions: [], administratorId: '', from: '', to: '' };

const displayDate = (value) => new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium', timeStyle: 'short',
}).format(new Date(value));

const AdminAuditSearch = () => {
  const [filters, setFilters] = useState(emptyFilters);
  const [submitted, setSubmitted] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const search = async (criteria, offset = 0) => {
    if (!Object.values(criteria).some((value) => Array.isArray(value) ? value.length > 0 : value.trim())) {
      setError('Enter at least one search filter.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const nextResult = await adminDb.searchAudit(criteria, offset);
      setSubmitted(criteria);
      setResult(nextResult);
    } catch {
      setError('Audit search failed. Check the family identifier, administrator UUID, and dates.');
    } finally {
      setLoading(false);
    }
  };

  const update = (field) => (event) =>
    setFilters((current) => ({ ...current, [field]: event.target.value }));

  const toggleAction = (action) => setFilters((current) => ({
    ...current,
    actions: current.actions.includes(action)
      ? current.actions.filter((value) => value !== action)
      : [...current.actions, action],
  }));

  return <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <h2 className="text-lg font-semibold">Audit search</h2>
    <p className="mt-1 text-sm text-slate-600">Search administrator activity, including retained history for purged families. Family code and UUID matches are exact. Results are limited to 25 per page.</p>
    <form className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5" onSubmit={(event) => {
      event.preventDefault();
      search({ ...filters });
    }}>
      <input className="rounded-lg border border-slate-300 p-2.5" aria-label="Audit family code or UUID" placeholder="Family code or UUID" value={filters.family} onChange={update('family')} />
      <details className="relative min-w-0">
        <summary className="cursor-pointer rounded-lg border border-slate-300 p-2.5" aria-label="Select audit actions">{filters.actions.length ? `${filters.actions.length} actions selected` : 'All actions'}</summary>
        <div className="absolute z-10 mt-1 max-h-72 w-72 max-w-[90vw] overflow-y-auto rounded-lg border border-slate-300 bg-white p-3 shadow-lg">
          <button className="mb-2 text-sm text-blue-700" type="button" onClick={() => setFilters((current) => ({ ...current, actions: [] }))}>Clear selection (all actions)</button>
          {actionGroups.map((group) => <div className="border-t border-slate-100 py-2" key={group.label}>
            <p className="mb-1 text-xs font-semibold uppercase text-slate-500">{group.label}</p>
            {group.values.map((action) => <label className="flex cursor-pointer items-center gap-2 py-1 text-sm" key={action}>
              <input type="checkbox" checked={filters.actions.includes(action)} onChange={() => toggleAction(action)} />
              <span>{action.replaceAll('_', ' ')}</span>
            </label>)}
          </div>)}
        </div>
      </details>
      <input className="rounded-lg border border-slate-300 p-2.5" aria-label="Audit administrator UUID" placeholder="Administrator UUID" value={filters.administratorId} onChange={update('administratorId')} />
      <input className="rounded-lg border border-slate-300 p-2.5" aria-label="Audit from date" type="date" value={filters.from} onChange={update('from')} />
      <input className="rounded-lg border border-slate-300 p-2.5" aria-label="Audit through date" type="date" value={filters.to} onChange={update('to')} />
      <button className="rounded-lg bg-blue-700 px-4 py-2.5 font-semibold text-white disabled:opacity-50 sm:col-span-2 lg:col-span-1" type="submit" disabled={loading}>Search audit</button>
    </form>
    {loading && <p className="mt-4 text-sm text-slate-600" role="status">Searching audit history…</p>}
    {error && <p className="mt-4 text-sm text-red-700" role="alert">{error}</p>}
    {result && !loading && <div className="mt-5">
      {result.rows.length === 0
        ? <p className="text-sm text-slate-600">No audit events matched these filters.</p>
        : <ul className="divide-y divide-slate-200 text-sm">{result.rows.map((event) =>
          <li className="py-3" key={event.id}>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <strong>{event.action}</strong>
              <span className={event.outcome === 'success' ? 'text-emerald-700' : 'text-red-700'}>{event.outcome}</span>
              <span className="text-slate-600">{displayDate(event.created_at)}</span>
            </div>
            <p className="mt-1 break-all text-slate-600">Family: {event.family_id || '—'} · Administrator: {event.administrator_id} ({event.administrator_role}) · Source: {event.source || 'admin_portal'}</p>
            {event.reason && <p className="mt-1 break-words">Reason: {event.reason}</p>}
            <details className="mt-2 text-slate-700">
              <summary className="cursor-pointer text-blue-700">Event details</summary>
              <div className="mt-2 grid gap-3 lg:grid-cols-2">
                <div><p className="font-medium">Previous values</p><pre className="mt-1 max-h-56 overflow-auto rounded bg-slate-50 p-2 text-xs">{JSON.stringify(event.previous_values, null, 2) || '—'}</pre></div>
                <div><p className="font-medium">New values</p><pre className="mt-1 max-h-56 overflow-auto rounded bg-slate-50 p-2 text-xs">{JSON.stringify(event.new_values, null, 2) || '—'}</pre></div>
              </div>
              <p className="mt-2 break-all text-xs">Request ID: {event.request_id} {event.provider_reference && `· Provider reference: ${event.provider_reference}`}</p>
            </details>
          </li>)}</ul>}
      <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3 text-sm">
        <span>Showing {result.rows.length ? result.offset + 1 : 0}–{result.offset + result.rows.length}</span>
        <div className="flex gap-3">
          <button className="disabled:text-slate-400" disabled={loading || result.offset === 0} onClick={() => search(submitted, Math.max(0, result.offset - 25))}>Previous</button>
          <button className="disabled:text-slate-400" disabled={loading || !result.has_more} onClick={() => search(submitted, result.offset + 25)}>Next</button>
        </div>
      </div>
    </div>}
  </section>;
};

export default AdminAuditSearch;
